import PersonIcon from '@mui/icons-material/Person';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { APP_PATH } from '@src/constants';
import useIsLoggedIn from '@src/hooks/useIsLoggedIn';
import { useClientInformationStore } from '@src/store';
import {
	clearPendingPostLoginRedirect,
	clearRedirectOnSchedulingRoute,
	resetClearedPostLoginRedirect,
} from '@src/utils/post-login-redirect-session';

const normalizeSchedulingRedirectPath = (pathname: string): string => {
	if (!APP_PATH || APP_PATH === '/') {
		return pathname;
	}

	if (pathname === APP_PATH) {
		return '/';
	}

	if (pathname.startsWith(`${APP_PATH}/`)) {
		const strippedPath = pathname.slice(APP_PATH.length);
		return strippedPath || '/';
	}

	return pathname;
};

const WelcomeBackBanner = () => {
	const router = useRouter();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const { isLoggedIn } = useIsLoggedIn();
	const firstName = useClientInformationStore((store) => store.firstName);
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

	const isOnUserPortal = !!globalThis.location?.pathname?.includes('/user-portal');

	useEffect(() => {
		if (!isOnUserPortal) {
			clearRedirectOnSchedulingRoute();
		}
	}, [isOnUserPortal]);

	if (isOnUserPortal) {
		return null;
	}

	let bannerLabel = 'Login';
	const bannerButtonTestId = isLoggedIn ? 'welcome-back-banner' : 'login-button';
	const bannerIconTestId = isLoggedIn ? 'welcome-back-banner-icon' : 'login-button-icon';

	if (isLoggedIn) {
		bannerLabel = isMobile || !firstName ? 'Welcome back' : `Welcome back, ${firstName}!`;
	}

	const handleBannerClick = () => {
		if (!isLoggedIn) {
			clearPendingPostLoginRedirect();
			resetClearedPostLoginRedirect();
			const { pathname = '', search = '', hash = '' } = globalThis.location || {};
			const normalizedPathname = normalizeSchedulingRedirectPath(pathname);
			const redirectTo = `${normalizedPathname}${search}${hash}`;
			const loginPath =
				redirectTo.length > 0
					? `/user-portal/login?redirectTo=${encodeURIComponent(redirectTo)}`
					: '/user-portal/login';

			router.push(loginPath);
			return;
		}

		setIsConfirmModalOpen(true);
	};

	const handleConfirmYes = () => {
		setIsConfirmModalOpen(false);
		router.push('/user-portal');
	};

	return (
		<>
			<Button
				data-test-id={bannerButtonTestId}
				onClick={handleBannerClick}
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 0.5,
					textTransform: 'none',
					color: 'text.interactive',
					p: 0,
					minWidth: 'unset',
					'&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
				}}
			>
				<PersonIcon fontSize="small" data-test-id={bannerIconTestId} />
				<Typography variant="bodySmallBook" color="text.interactive" data-test-id="welcome-back-banner-label">
					{bannerLabel}
				</Typography>
			</Button>

			{isLoggedIn && (
				<Dialog
					open={isConfirmModalOpen}
					onClose={() => setIsConfirmModalOpen(false)}
					aria-labelledby="welcome-back-confirm-title"
					data-test-id="welcome-back-confirm-modal"
				>
					<DialogTitle id="welcome-back-confirm-title">
						<Typography variant="bodyLargeSemiBold">Are you sure?</Typography>
					</DialogTitle>
					<DialogContent>
						<Typography variant="bodyMediumBook">
							Leaving will take you to the client portal. Any unsaved progress will be lost.
						</Typography>
					</DialogContent>
					<DialogActions sx={{ gap: 1, px: 3, pb: 3, justifyContent: 'flex-end' }}>
						<Button
							data-test-id="welcome-back-confirm-no"
							variant="secondaryDefault"
							size="S"
							onClick={() => setIsConfirmModalOpen(false)}
						>
							No
						</Button>
						<Button
							data-test-id="welcome-back-confirm-yes"
							variant="primaryDefault"
							size="S"
							onClick={handleConfirmYes}
						>
							Yes
						</Button>
					</DialogActions>
				</Dialog>
			)}
		</>
	);
};

export default WelcomeBackBanner;
